import React from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { Typography } from '@mui/material'
import { Spinner } from 'src/components/spinner'
import { TitledList } from 'src/components/sidebar-panels/titled-list'
import { CustomErrorBoundary } from 'src/components/errors/error-boundary'
import { PlaylistAdd } from '@emotion-icons/material/PlaylistAdd'
import { Edit } from '@emotion-icons/boxicons-solid/Edit'
import { ListUl } from '@emotion-icons/fa-solid/ListUl'
import { UserList } from 'src/common-services'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { useAuthData } from 'src/components/auth/util'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'

import { userLists } from 'src/atoms/user-lists'
import * as userListsModalAtoms from 'src/atoms/user-lists/user-lists-modal'
import * as authAtoms from 'src/atoms/auth'
import * as createUserListModalAtoms from 'src/atoms/user-lists/user-list-modal'
import { GenericLoader, LoaderRow } from 'src/v2/components/skeleton-loaders'

const UserListsEmptyWrapper = styled.div`
  padding: ${Shapes.spacing[4]}px ${Shapes.spacing[4]}px;
  padding-bottom: 0;
`
const SpinnerWrapper = styled.div`
  padding: ${Shapes.spacing[4]}px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ListItemWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[4]}px;
  :hover {
    background-color: ${Colors.neutral50};
  }
`

const ListItemIconWrapper = styled.div`
  display: flex;
  margin-right: 10px;
`

const ListItemName = styled.span`
  font-size: 14px;
  color: ${Colors.neutral700};
`

export const ListItemCount = styled.span`
  margin-left: 6px;
  color: ${Colors.textDark};
`

const UserListsWrapper = styled.div``
const UserListButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[4]}px;
`

type UserListsItemProps = Partial<UserList> & { onClick: () => void }

const UserListsItem: React.FC<UserListsItemProps> = ({ name, items, onClick }) => (
  <ListItemWrapper onClick={onClick}>
    <ListItemIconWrapper>
      <ListUl color={Colors.neutral400} size={14} />
    </ListItemIconWrapper>
    <ListItemName>{name}</ListItemName>
    <ListItemCount>({items?.length || 0})</ListItemCount>
  </ListItemWrapper>
)

const UserListsEmpty: React.FC<{ noUser: boolean }> = ({ noUser }) => {
  return (
    <UserListsEmptyWrapper>
      <Typography variant="title2m" color={Colors.textMedium}>
        You have not created a new list for your saved locations yet.
        {noUser && ' Sign up or login to create a list.'}
      </Typography>
    </UserListsEmptyWrapper>
  )
}

const renderUserListItem = (onClick: (item: UserList) => void) => (item: UserList) => (
  <UserListsItem key={item.id} name={item.name} items={item.items} onClick={() => onClick(item)} />
)

const Inner: React.FC = () => {
  const lists = useRecoilValue(userLists)
  const setUserListsModalOpen = useSetRecoilState(userListsModalAtoms.open)
  const setCreateUserListState = useSetRecoilState(createUserListModalAtoms.state)
  const setUserListsModal = useSetRecoilState(userListsModalAtoms.open)
  const setSelectedUserListId = useSetRecoilState(userListsModalAtoms.selectedListId)
  const setAuthModalOpen = useSetRecoilState(authAtoms.modalOpen)
  const authData = useAuthData()

  const handleClickUserListItem = (userList: UserList) => {
    setUserListsModal(true)
    setSelectedUserListId(userList.id)
  }
  const handleClickManage = () => setUserListsModalOpen(true)
  const handleClickAuth = () => setAuthModalOpen(true)
  return (
    <TitledList
      title={
        <FlexRow flex={'1'} align="center" justify="space-between">
          <Typography variant="title2">Your Lists</Typography>
          <UserListButtons>
            {authData ? (
              <>
                <ButtonWithIcon color={Colors.primaryGreen400} onClick={() => setCreateUserListState({})}>
                  <PlaylistAdd size={14} />
                  New
                </ButtonWithIcon>
                {lists && lists.length >= 1 && (
                  <ButtonWithIcon onClick={handleClickManage}>
                    <Edit size={14} />
                    Manage
                  </ButtonWithIcon>
                )}
              </>
            ) : (
              <ButtonWithIcon onClick={handleClickAuth}>Sign Up / Login</ButtonWithIcon>
            )}
          </UserListButtons>
        </FlexRow>
      }>
      <UserListsWrapper>
        {lists ? (
          lists.length >= 1 ? (
            lists.map(renderUserListItem(handleClickUserListItem))
          ) : (
            <UserListsEmpty noUser={!authData} />
          )
        ) : (
          <FlexColumn
            gap={12}
            style={{
              padding: 16,
            }}>
            <LoaderRow alignItems="center" gap={8}>
              <GenericLoader variant="rectangular" color="medium" height={18} width={18} />
              <GenericLoader height={20} width={150} />
            </LoaderRow>
            <LoaderRow alignItems="center" gap={8}>
              <GenericLoader variant="rectangular" color="medium" height={18} width={18} />
              <GenericLoader height={20} width={150} />
            </LoaderRow>
            <LoaderRow alignItems="center" gap={8}>
              <GenericLoader variant="rectangular" color="medium" height={18} width={18} />
              <GenericLoader height={20} width={150} />
            </LoaderRow>
          </FlexColumn>
        )}
      </UserListsWrapper>
    </TitledList>
  )
}

const ErrorDisplay = styled.div`
  padding: ${Shapes.spacing[1]}px ${Shapes.spacing[5]}px;
`

export const YourLists: React.FC = () => {
  return (
    <CustomErrorBoundary errorDisplay={<ErrorDisplay>Your Lists are currently not available.</ErrorDisplay>}>
      <React.Suspense
        fallback={
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        }>
        <Inner />
      </React.Suspense>
    </CustomErrorBoundary>
  )
}
